// externals
import { useRef } from 'react';
import type { FC, FocusEvent } from 'react';

// libraries
import { Typography } from '@interstate/components/Typography';
import { Grid } from '@interstate/components/Grid';
import { ComboBox, type ComboBoxOptions } from '@interstate/components/ComboBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Action } from '@interstate/components/Action';
import { PlusIcon } from '@interstate/components/Icons/PlusIcon';
import { XMarkIcon } from '@interstate/components/Icons/XMarkIcon';
import { FlexCol, FlexRow } from '@makemydeal/dr-dash-components';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { selectFocusedInput, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import {
    ItemsListContainer as Container,
    ReconItemRow,
    ItemInputWrapper as InputWrapper,
    ItemRemoveButtonWrapper as RemoveButtonWrapper
} from '../tradeReconCost.styles';

import type { TradeReconItemsListProps } from '@makemydeal/dr-dash-store/types/tradeReconCostTypes';
import { MAX_RECON_ITEMS } from '../../../constants';

export const TradeReconItemsList: FC<TradeReconItemsListProps> = ({
    tradeKey,
    reconItems,
    reconItemOptions,
    onNameChange,
    onAmountChange,
    onRemove,
    onAdd
}) => {
    const isMaxReached = reconItems.length >= MAX_RECON_ITEMS;
    const isAutoSelectEnabled = useSelectOnFocusEnabled(featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap);

    const handleNameFocus = (e: FocusEvent<HTMLElement>) => {
        if (isAutoSelectEnabled) selectFocusedInput(e);
    };

    // Tracks the raw text the user is typing in each ComboBox input (keyed by item.id).
    // Stored in a ref so we never trigger re-renders for transient typing state.
    const inputTextsRef = useRef<Record<string, string>>({});

    // Record every keystroke so we can apply it on blur.
    const handleInputChange = (itemId: string) => (event: InterstateOnChangeEvent<string>) => {
        inputTextsRef.current[itemId] = event.target.value;
    };

    // When the user picks an option (including the built-in "Create …" chip) clear the
    // pending text so that the subsequent blur does not double-apply the value.
    const handleChange = (tradeKey: string, itemId: string) => (event: InterstateOnChangeEvent<ComboBoxOptions>) => {
        delete inputTextsRef.current[itemId];
        onNameChange(tradeKey, itemId)(event);
    };

    // When the ComboBox loses focus WITHOUT a dropdown selection, auto-confirm whatever
    // the user typed (blur fires with the previously selected options, not the typed text,
    // so we rely on inputTextsRef to get the pending value).
    const handleBlur = (tradeKey: string, itemId: string) => (event: InterstateOnChangeEvent<ComboBoxOptions>) => {
        const typedText = inputTextsRef.current[itemId]?.trim();
        if (typedText) {
            const syntheticEvent: InterstateOnChangeEvent<ComboBoxOptions> = {
                target: {
                    value: [{ label: typedText, value: typedText }],
                    name: event.target.name
                }
            };
            onNameChange(tradeKey, itemId)(syntheticEvent);
        }
        delete inputTextsRef.current[itemId];
    };
    return (
        <Container>
            <FlexCol>
                <FlexRow justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Reconditioning Cost Justification</Typography>
                </FlexRow>
            </FlexCol>

            {/* Recon Items */}
            {reconItems.map((item, itemIndex) => (
                <ReconItemRow key={item.id || `item-${itemIndex}`}>
                    <InputWrapper>
                        <Grid xs={12} sm={6} md={6} lg={4} xl={3} onFocus={handleNameFocus}>
                            <ComboBox
                                label="Name"
                                name={`recon-name-${itemIndex}`}
                                data-testid={`${tradeKey}-recon-name-${itemIndex}`}
                                options={reconItemOptions}
                                onChange={handleChange(tradeKey, item.id)}
                                onInputChange={handleInputChange(item.id)}
                                onBlur={handleBlur(tradeKey, item.id)}
                                size="sm"
                                allowNewEntry
                                value={
                                    item.reconditionName
                                        ? reconItemOptions.filter((option) => option.value === item.reconditionName).length > 0
                                            ? reconItemOptions.filter((option) => option.value === item.reconditionName)
                                            : [{ label: item.reconditionName, value: item.reconditionName }]
                                        : []
                                }
                            />
                        </Grid>
                    </InputWrapper>
                    <InputWrapper>
                        <Grid xs={12} sm={6} md={6} lg={4} xl={3}>
                            <NumericInput
                                label="Amount"
                                name={`recon-amount-${itemIndex}`}
                                value={item.reconditionAmount.toString()}
                                onChange={onAmountChange(tradeKey, item.id)}
                                inputPrefix="$"
                                allowNegative={false}
                                data-testid={`${tradeKey}-recon-amount-${itemIndex}`}
                                size="sm"
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </Grid>
                    </InputWrapper>
                    <RemoveButtonWrapper>
                        <Action
                            onClick={() => onRemove(tradeKey, item.id)}
                            actionIcon={{
                                icon: <XMarkIcon />,
                                start: true
                            }}
                            data-testid={`${tradeKey}-remove-recon-${itemIndex}`}
                        />
                    </RemoveButtonWrapper>
                </ReconItemRow>
            ))}

            {/* Add Recon Item Button */}
            <Action
                onClick={() => onAdd(tradeKey)}
                actionIcon={{
                    icon: <PlusIcon />,
                    start: true
                }}
                data-testid={`${tradeKey}-add-recon-item`}
                disabled={isMaxReached}
            >
                Add Recon Item
            </Action>
            {isMaxReached && <Typography variant="h6">You can add up to 15 recon items only.</Typography>}
        </Container>
    );
};

export default TradeReconItemsList;
